import type { EnvironmentResource, IId, ResourceWithSlug, WorkerPoolResource } from "@octopusdeploy/octopus-server-client";
import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import type { BaseConfiguration, MonitorConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationPage";
import { generateAgentAsTargetNamespace, generateAgentAsWorkerNamespace } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/KubernetesAgentHelperMethods";
import type { TargetConfigurationResource } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/KubernetesAgentTargetConfigurationDialog";
import type { WorkerConfigurationResource } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/KubernetesAgentWorkerConfigurationDialog";
import type { Language, TextFormat } from "~/components/CodeEditor/CodeEditor";
import { escapeAllDoubleQuotes, generateReleaseName, getValidName } from "~/utils/HelmCommands/HelmCommandHelperMethods";
import type { HelmUpgradeCommandValue } from "~/utils/HelmCommands/HelmUpgradeCommandBuilder";
import { HelmUpgradeCommandBuilder } from "~/utils/HelmCommands/HelmUpgradeCommandBuilder";
import { KubernetesAgentInstallationCommandBuilder } from "./KubernetesAgentInstallationCommandBuilder";
type ResourceWithSlugAndId = ResourceWithSlug & IId;
// This value represents the highest major version of the Kubernetes Agent Helm Chart
// that is supported by this version of Server.
// If this value is being bumped, please also bump the value in the backend:
// KubernetesAgentUpgradeManager.LatestSupportedMajorVersion
// Note that while the "kubernetes-agent-as-worker" feature flag is in use, this isn't used.
const kubernetesAgentLatestSupportedMajorVersion = 2;
const registryUrl: string = "oci://registry-1.docker.io/octopusdeploy/kubernetes-agent";
function getSlugsForIds(resourceIds: string[], resources: ResourceWithSlugAndId[]) {
    return resourceIds
        .map((id) => resources.find((res) => res.Id === id)?.Slug ?? "")
        .filter((slug) => slug !== "") // This shouldn't do anything, because slugs are required on environments
        .join(",");
}
function getMachineTagsList(machineTags: string[]) {
    // Machine tags can contain double quotes, so we'll escape them so they don't break the helm command
    return machineTags.map((mr) => escapeAllDoubleQuotes(mr)).join(",");
}
function validateModel(model: BaseConfiguration) {
    if (!model.Space) {
        throw new Error("Attempted to render Kubernetes Agent Configuration Dialog outside of a space. This should never happen.");
    }
}
function createHelmInstallAgentCommand(language: ScriptingLanguage | Language | TextFormat, model: BaseConfiguration, namespace: string, helmCommandValues: HelmUpgradeCommandValue[]) {
    validateModel(model);
    const lineBreak = language === ScriptingLanguage.Bash ? "\\" : "`";
    const releaseName = generateReleaseName(model.Name);
    const storageClassName = model.StorageClassName.trim();
    if (!!storageClassName) {
        helmCommandValues.push({ key: "persistence.storageClassName", value: storageClassName });
    }
    return (new KubernetesAgentInstallationCommandBuilder(`${kubernetesAgentLatestSupportedMajorVersion}.*.*`, namespace, releaseName, registryUrl, lineBreak)
        //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .withCommonHelmSetValues(model.Space!.Name, model.ServerUri, model.ServerNodeCommsAddresses.map((n) => n.commsAddress).join(","), model.AccessToken)
        .withValues(helmCommandValues)
        .toCommandString());
}
function createHelmUpgradeCommand(language: ScriptingLanguage | Language | TextFormat, releaseName: string, namespace: string, values: HelmUpgradeCommandValue[]) {
    const lineBreak = language === ScriptingLanguage.Bash ? "\\" : "`";
    return new HelmUpgradeCommandBuilder(`${kubernetesAgentLatestSupportedMajorVersion}.*.*`, namespace, releaseName, registryUrl, lineBreak, false, false).withFlag("--reset-then-reuse-values").withValues(values).toCommandString();
}
export function createTargetHelmCommand(language: ScriptingLanguage | Language | TextFormat, model: TargetConfigurationResource, environments: EnvironmentResource[], enableMonitor: boolean) {
    validateModel(model);
    const machineTags = getMachineTagsList(model.MachineRoles);
    const environmentSlugs = getSlugsForIds(model.EnvironmentIds, environments);
    const defaultNamespace = model.DefaultNamespace.trim();
    const agentName = getValidName(model.Name);
    const namespace = generateAgentAsTargetNamespace(model.Name);
    const targetSpecificValues: HelmUpgradeCommandValue[] = [];
    //add target specific values
    targetSpecificValues.push({ key: "agent.name", value: agentName });
    targetSpecificValues.push({ key: "agent.deploymentTarget.initial.environments", value: `{${environmentSlugs}}` });
    targetSpecificValues.push({ key: "agent.deploymentTarget.initial.tags", value: `{${machineTags}}` });
    targetSpecificValues.push({ key: "agent.deploymentTarget.enabled", value: "true" });
    if (defaultNamespace) {
        targetSpecificValues.push({ key: "agent.deploymentTarget.initial.defaultNamespace", value: defaultNamespace });
    }
    if (enableMonitor && model.IncludeMonitorHelmCommands) {
        targetSpecificValues.push(...getKubernetesMonitorValues(agentName, model));
    }
    return createHelmInstallAgentCommand(language, model, namespace, targetSpecificValues);
}
export function createWorkerHelmCommand(language: ScriptingLanguage | Language | TextFormat, model: WorkerConfigurationResource, workerPools: WorkerPoolResource[]) {
    const workerPoolSlugs = getSlugsForIds(model.WorkerPools, workerPools);
    const agentName = getValidName(model.Name);
    const namespace = generateAgentAsWorkerNamespace(model.Name);
    const workerSpecificHelmValues: HelmUpgradeCommandValue[] = [
        { key: "agent.name", value: agentName },
        { key: "agent.worker.enabled", value: "true" },
        { key: "agent.worker.initial.workerPools", value: `{${workerPoolSlugs}}` },
    ];
    return createHelmInstallAgentCommand(language, model, namespace, workerSpecificHelmValues);
}
export function createMonitorUpgradeHelmCommand(language: ScriptingLanguage | Language | TextFormat, releaseName: string, namespace: string, configuration: MonitorConfiguration) {
    const agentName = getValidName(configuration.Name);
    return createHelmUpgradeCommand(language, releaseName, namespace, getKubernetesMonitorValues(agentName, configuration));
}
function getKubernetesMonitorValues(agentName: string, configuration: MonitorConfiguration) {
    const values: HelmUpgradeCommandValue[] = [];
    // add monitor installation values
    values.push({ key: "kubernetesMonitor.enabled", value: "true" });
    values.push({ key: "kubernetesMonitor.registration.serverApiUrl", value: configuration.ServerUri });
    values.push({ key: "kubernetesMonitor.monitor.serverGrpcUrl", value: configuration.ServerGrpcUri });
    values.push({ key: "kubernetesMonitor.registration.serverAccessToken", value: configuration.AccessToken });
    //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    values.push({ key: "kubernetesMonitor.registration.spaceId", value: configuration.SpaceId });
    values.push({ key: "kubernetesMonitor.registration.machineName", value: agentName });
    return values;
}
